export const PLAN_STATUS_OPTIONS = [
  { value: 'DRAFT', label: '草稿', tone: 'neutral' },
  { value: 'LIST_CONFIRMED', label: '名单已确认', tone: 'primary' },
  { value: 'CHAINS_GENERATED', label: '链路已生成', tone: 'amber' },
  { value: 'USER_CONFIRMED', label: '用户已确认', tone: 'emerald' },
  { value: 'NEEDS_RECONFIRM', label: '需重新确认', tone: 'red' }
]

export const NODE_ROLE_OPTIONS = [
  { value: 'START', label: '开端' },
  { value: 'MIDDLE', label: '中间' },
  { value: 'PLATFORM', label: '平台' }
]

export const ISSUE_SEVERITY_OPTIONS = [
  { value: 'ERROR', label: '错误', tone: 'red' },
  { value: 'WARNING', label: '提醒', tone: 'amber' },
  { value: 'INFO', label: '信息', tone: 'primary' }
]

export function statusLabel(value) {
  return PLAN_STATUS_OPTIONS.find(item => item.value === value)?.label || value || '-'
}

export function statusTone(value) {
  return PLAN_STATUS_OPTIONS.find(item => item.value === value)?.tone || 'neutral'
}

export function severityLabel(value) {
  return ISSUE_SEVERITY_OPTIONS.find(item => item.value === value)?.label || value || '-'
}

export function severityTone(value) {
  return ISSUE_SEVERITY_OPTIONS.find(item => item.value === value)?.tone || 'neutral'
}

export function roleLabel(value) {
  return NODE_ROLE_OPTIONS.find(item => item.value === value)?.label || value || '-'
}

export function formatInvoiceAmount(value) {
  if (value === null || value === undefined || value === '') return '-'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  return Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

// 按指定位数格式化金额（用于税额等需精确到分的场景）
export function formatInvoiceAmountTo(value, fractionDigits = 0) {
  if (value === null || value === undefined || value === '') return '-'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  const digits = Number.isFinite(Number(fractionDigits)) ? Number(fractionDigits) : 0
  return Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(amount)
}

export function formatWanAmount(value) {
  if (value === null || value === undefined || value === '') return '-'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  return `${Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount / 10000)} 万`
}

export function formatProfitRate(value) {
  if (value === null || value === undefined || value === '') return '-'
  const rate = Number(value)
  if (!Number.isFinite(rate)) return '-'
  return `${(rate * 100).toFixed(2)}%`
}

export function parsePercentInput(value) {
  if (value === null || value === undefined) return null
  const text = String(value).replace('%', '').trim()
  if (text === '') return null
  const numeric = Number(text)
  if (!Number.isFinite(numeric)) return null
  return numeric / 100
}

export function toPercentInput(value) {
  if (value === null || value === undefined || value === '') return ''
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ''
  return (numeric * 100).toFixed(2)
}

export function normalizeAmountInput(value) {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (text === '') return null
  const numeric = Number(text)
  if (!Number.isFinite(numeric)) return null
  return Math.round(numeric / 100) * 100
}

export function groupCustomersByRegion(customers, regions) {
  const result = new Map()
  for (const region of regions) {
    result.set(region.id, [])
  }
  for (const customer of customers) {
    if (!result.has(customer.regionBucketId)) {
      result.set(customer.regionBucketId, [])
    }
    result.get(customer.regionBucketId).push(customer)
  }
  for (const list of result.values()) {
    list.sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
  }
  return result
}

export function cleanPayload(payload) {
  return Object.entries(payload).reduce((next, [key, value]) => {
    if (value === undefined || value === '') return next
    next[key] = value
    return next
  }, {})
}

export {
  buildRegionMatrixRows,
  displayInvoiceCompanyName,
  findNewPlanCustomer,
  hasSameProductNames,
  invoiceDropAnchor,
  invoiceDropMovePayload,
  invoiceRegionTotalAmount,
  isFullProfitRateCustomer,
  isSameProfitRate
} from './matrixRules.mjs'

// ===== 链路矩阵相关纯函数 =====

export function isPlatformNode(node) {
  return node?.nodeRole === 'PLATFORM' || node?.role === 'PLATFORM' || node?.platform === true
}

export function sortChainNodes(nodes) {
  return [...(nodes || [])].sort((left, right) => {
    const leftOrder = left.displayOrder ?? left.nodeOrder ?? left.sequenceNo ?? 0
    const rightOrder = right.displayOrder ?? right.nodeOrder ?? right.sequenceNo ?? 0
    return leftOrder - rightOrder
  })
}

export function nonPlatformSubjects(chain) {
  return sortChainNodes(chain?.nodes || chain?.chainNodes || []).filter(node => !isPlatformNode(node))
}

export function platformNodeOf(chain) {
  return sortChainNodes(chain?.nodes || chain?.chainNodes || []).find(node => isPlatformNode(node)) || null
}

export function hasLockedNodeField(node) {
  return Boolean(
    node?.companyLocked ||
    node?.inputAmountLocked ||
    node?.outputAmountLocked ||
    node?.profitRateLocked ||
    node?.productNameLocked
  )
}

export function matrixColumnCount(chains, minChainSubjectCount) {
  const min = Number.isFinite(Number(minChainSubjectCount)) ? Number(minChainSubjectCount) : 10
  const maxSubjects = (chains || []).reduce(
    (max, chain) => Math.max(max, nonPlatformSubjects(chain).length),
    0
  )
  return Math.max(min, maxSubjects)
}

// 镜像后端 requireConfirmedListInput：DRAFT 或（NEEDS_RECONFIRM 且名单未确认）时不能生成
export function canGenerateChains(plan) {
  if (!plan) return false
  const status = plan.status
  if (status === 'DRAFT') return false
  if (status === 'NEEDS_RECONFIRM' && !plan.listConfirmedAt) return false
  return true
}

export function regionNameById(regions) {
  const map = new Map()
  for (const region of regions || []) {
    if (region?.id != null) map.set(String(region.id), region.regionName || '未命名区域')
  }
  return map
}

// 构建 chainId+nodeId / chainId+planCustomerId → { hasError, hasWarning } 索引
export function issueMarkByKey(issues) {
  const map = new Map()
  for (const issue of issues || []) {
    const chainId = issue?.chainId
    if (chainId == null) continue
    const severity = String(issue?.severity || issue?.level || '').toUpperCase()
    const isError = severity === 'ERROR'
    const isWarning = severity === 'WARNING'
    if (!isError && !isWarning) continue
    const keys = []
    if (issue?.nodeId != null) keys.push(`${chainId}:${issue.nodeId}`)
    if (issue?.planCustomerId != null) keys.push(`${chainId}:pid:${issue.planCustomerId}`)
    for (const key of keys) {
      const prev = map.get(key) || { hasError: false, hasWarning: false }
      prev.hasError = prev.hasError || isError
      prev.hasWarning = prev.hasWarning || isWarning
      map.set(key, prev)
    }
  }
  return map
}

export function issueMarkFor(markMap, chainId, node) {
  if (!markMap || chainId == null || !node) return null
  const byNode = node.id != null ? markMap.get(`${chainId}:${node.id}`) : null
  const byPid = node.planCustomerId != null ? markMap.get(`${chainId}:pid:${node.planCustomerId}`) : null
  if (byNode && byPid) {
    return {
      hasError: byNode.hasError || byPid.hasError,
      hasWarning: byNode.hasWarning || byPid.hasWarning
    }
  }
  return byNode || byPid || null
}

